/** Place names are drawn at this size in screen px, with every Chinese glyph one em wide. */
export const CHINA_MAP_LABEL_FONT_SIZE = 12;
/**
 * Half the side, in ems, of the square each character keeps clear of its region's outline: its em
 * box plus a quarter of a pixel of padding at full size. Chinese glyphs fill their em box almost
 * edge to edge (PingFang's ink spans 0.03–0.97 em across and 0.95 em down), so with the ink's own
 * inset this keeps a character just clear of the border's stroke. A wider margin would cost names
 * their full size in provinces that only just hold them.
 */
export const CHINA_MAP_LABEL_GLYPH_HALF_SIZE = 0.5 + 0.25 / CHINA_MAP_LABEL_FONT_SIZE;

/**
 * One way of writing a region's name inside its outline: where each character goes. The characters
 * need not sit on one line or in one column, as long as they read in order (see
 * `findChinaMapLabelLayouts`).
 */
export interface ChinaMapLabelLayout {
  /** The middle of the name, in map units. */
  anchor: readonly [number, number];
  /**
   * The largest font size, in map units, at which every character's square
   * (`CHINA_MAP_LABEL_GLYPH_HALF_SIZE`) is inside the outline. The name also fits at the smaller
   * sizes this layout is drawn at.
   */
  em: number;
  /**
   * Set when each character has its own spot in the region, rather than a place in a line. The
   * characters then stay on their spots as the font shrinks, `offsets` always being multiples of
   * `em`. In any other layout they close in on the anchor, `offsets` being multiples of the font
   * size drawn.
   */
  isScattered?: boolean;
  /** Each character's center, in ems from the anchor, in reading order. */
  offsets: readonly (readonly [number, number])[];
}

/**
 * How far apart, in map units, a layout's `offsets` are when the name is drawn at `fontSize`
 * (also in map units).
 */
export function getChinaMapLabelOffsetUnit(layout: ChinaMapLabelLayout, fontSize: number): number {
  return layout.isScattered === true ? layout.em : fontSize;
}

/** Recenters character positions on the middle of their bounds. */
export function centerChinaMapLabelOffsets(
  positions: readonly (readonly [number, number])[],
): [number, number][] {
  const xs = positions.map(([x]) => x);
  const ys = positions.map(([, y]) => y);
  const centerX = (Math.min(...xs) + Math.max(...xs)) / 2;
  const centerY = (Math.min(...ys) + Math.max(...ys)) / 2;
  return positions.map(([x, y]) => [x - centerX, y - centerY]);
}

/** A name written across in one line, one em per character: the layout a name gets with room. */
export function getChinaMapLabelLineOffsets(characterCount: number): [number, number][] {
  return centerChinaMapLabelOffsets(
    Array.from({ length: characterCount }, (_, index) => [index, 0]),
  );
}
